import {useCallback, useEffect, useState} from 'react';
import {getConfigText as fetchConfigText, saveConfigText as persistConfigText} from '../services/configTextApi';
import {useAsyncAction} from './useAsyncAction';

/**
 * Encapsulates config documentation (markdown) fetching, editing, and saving.
 *
 * @param {string} edgeId
 */
export const useConfigText = (edgeId) => {
  const {loading, error, run} = useAsyncAction();
  const [content, setContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetch = useCallback(async () => {
    const result = await run(
      () => fetchConfigText(edgeId),
      'Unable to load documentation. You may not have permission.',
    );
    if (result.ok) {
      setContent(result.data.value);
      setOriginalContent(result.data.value);
      if (result.data.updatedAt) setLastUpdated(result.data.updatedAt);
    }
  }, [edgeId, run]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const save = useCallback(async () => {
    const result = await run(
      () => persistConfigText(edgeId, content),
      'Unable to save documentation. You may not have permission.',
    );
    if (result.ok) {
      setOriginalContent(content);
      setLastUpdated(new Date());
      setIsEditing(false);
    }
  }, [edgeId, content, run]);

  const cancel = useCallback(() => {
    setContent(originalContent);
    setIsEditing(false);
  }, [originalContent]);

  return {loading, error, content, setContent, isEditing, setIsEditing, lastUpdated, save, cancel};
};

